import AddIcon from '@mui/icons-material/Add';
import PeopleIcon from '@mui/icons-material/People';
import SearchIcon from '@mui/icons-material/Search';
import { Alert, Box, Button, InputAdornment, Stack, TextField } from '@mui/material';
import { useDeferredValue, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DataTableSkeleton } from '@/components/common/DataTableSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadMoreButton } from '@/components/common/LoadMoreButton';
import { PageHeader } from '@/components/common/PageHeader';
import { useNotify } from '@/hooks/useNotify';
import { isApiError } from '@/lib/apiError';

import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';
import { ProfesionalConEstablecimientosDialog } from '../components/ProfesionalConEstablecimientosDialog';
import { ProfesionalesTable } from '../components/ProfesionalesTable';
import { useDeleteProfesional, useProfesionales } from '../hooks/useProfesionales';
import type { Profesional, ProfesionalListParams } from '../types';

const PROFESIONAL_CON_ESTAB_ERROR = 'profesional_con_establecimientos';

export function ProfesionalesPage(): JSX.Element {
  const navigate = useNavigate();
  const notify = useNotify();

  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [sortBy, setSortBy] =
    useState<NonNullable<ProfesionalListParams['sort_by']>>('nombre_apellido');
  const [sortOrder, setSortOrder] =
    useState<NonNullable<ProfesionalListParams['sort_order']>>('asc');

  const params: ProfesionalListParams = {
    q: deferredSearch.trim() || undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
    limit: 20,
  };

  const { items, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, error, refetch } =
    useProfesionales(params);

  const [profesionalToDelete, setProfesionalToDelete] = useState<Profesional | null>(null);
  const [profesionalConEstabDialog, setProfesionalConEstabDialog] = useState<Profesional | null>(null);
  const deleteMutation = useDeleteProfesional();

  function handleSortChange(
    newSortBy: NonNullable<ProfesionalListParams['sort_by']>,
    newSortOrder: NonNullable<ProfesionalListParams['sort_order']>
  ): void {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  }

  async function handleConfirmDelete(): Promise<void> {
    if (!profesionalToDelete) return;
    try {
      await deleteMutation.mutateAsync(profesionalToDelete.uid_profesional);
      notify.success(`Profesional "${profesionalToDelete.nombre_apellido}" eliminado.`);
      setProfesionalToDelete(null);
    } catch (err) {
      if (isApiError(err) && err.errorCode === PROFESIONAL_CON_ESTAB_ERROR) {
        setProfesionalConEstabDialog(profesionalToDelete);
        setProfesionalToDelete(null);
        return;
      }
      notify.fromError(err, 'Error al eliminar el profesional.');
    }
  }

  return (
    <>
      <PageHeader
        title="Profesionales"
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/profesionales/nuevo')}
          >
            Nuevo profesional
          </Button>
        }
      />

      <Stack spacing={3}>
        <TextField
          placeholder="Buscar por nombre, apellido, localidad..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        {isLoading && <DataTableSkeleton columns={5} rows={5} />}

        {isError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void refetch()}>
                Reintentar
              </Button>
            }
          >
            {error instanceof Error
              ? error.message
              : 'Error al cargar la lista de profesionales.'}
          </Alert>
        )}

        {!isLoading && !isError && items.length === 0 && (
          <EmptyState
            icon={<PeopleIcon sx={{ fontSize: 'inherit' }} />}
            title={
              deferredSearch.trim()
                ? 'No hay resultados para tu búsqueda'
                : 'No hay profesionales todavía'
            }
            description={
              deferredSearch.trim()
                ? 'Probá con otros términos o limpiá la búsqueda.'
                : 'Crea el primer profesional para empezar.'
            }
            action={
              !deferredSearch.trim() ? (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/profesionales/nuevo')}
                >
                  Crear profesional
                </Button>
              ) : undefined
            }
          />
        )}

        {!isLoading && !isError && items.length > 0 && (
          <Box>
            <ProfesionalesTable
              items={items}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
              onDelete={(p) => setProfesionalToDelete(p)}
            />
            <LoadMoreButton
              hasMore={hasNextPage}
              isLoading={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
            />
          </Box>
        )}
      </Stack>

      {profesionalToDelete && (
        <DeleteConfirmDialog
          open={true}
          nombre={profesionalToDelete.nombre_apellido}
          onConfirm={() => void handleConfirmDelete()}
          onCancel={() => setProfesionalToDelete(null)}
          loading={deleteMutation.isPending}
        />
      )}

      {profesionalConEstabDialog && (
        <ProfesionalConEstablecimientosDialog
          open={true}
          uidProfesional={profesionalConEstabDialog.uid_profesional}
          nombre={profesionalConEstabDialog.nombre_apellido}
          onClose={() => setProfesionalConEstabDialog(null)}
        />
      )}
    </>
  );
}
